import { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { PinKeypad } from '@/components/auth/PinKeypad';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { AlertCircle } from 'lucide-react';

export default function LoginPage() {
    const { loginUser, role, mustChangePin } = useAuth();
    const nav = useNavigate();
    const [nickname, setNickname] = useState('');
    const [pin, setPin] = useState('');
    const [err, setErr] = useState('');
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (role === 'user') nav(mustChangePin ? '/change-pin' : '/dashboard', { replace: true });
    }, [role, mustChangePin, nav]);

    const submit = async (usedPin) => {
        setErr('');
        if (!nickname.trim()) { setErr('Ingresa tu nickname'); return; }
        const p = usedPin || pin;
        if (!p || p.length !== 4) { setErr('Ingresa tu PIN de 4 dígitos'); return; }
        setBusy(true);
        try {
            const data = await loginUser(nickname.trim(), p);
            toast.success(`Bienvenido, @${data.user.nickname}`);
            if (data.must_change_pin) nav('/change-pin', { replace: true });
            else nav('/dashboard', { replace: true });
        } catch (e) {
            setErr(e?.response?.data?.detail || 'Error al iniciar sesión');
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="min-h-screen flex">
            {/* Left hero panel */}
            <div className="hidden lg:flex flex-1 hero-mist items-center justify-center px-10">
                <div className="max-w-md">
                    <img src="/assets/oklovendo-logo.png" alt="Ok te lo vendo" className="h-14 w-auto mb-6" onError={e => { e.target.style.display = 'none'; }} />
                    <h2 className="text-3xl font-semibold tracking-tight">Compra y vende sin fricción.</h2>
                    <p className="mt-2 text-muted-foreground">
                        Ingresa con tu nickname y PIN. Si es tu primer acceso, tu PIN inicial es <span className="font-mono font-semibold">9999</span>.
                    </p>
                </div>
            </div>

            {/* Right form panel */}
            <div className="flex-1 flex items-center justify-center px-4 py-10">
                <div className="w-full max-w-sm">
                    {/* Logo (mobile + all) */}
                    <div className="flex justify-center mb-5">
                        <img src="/assets/oklovendo-logo.png" alt="Ok te lo vendo" className="h-10 w-auto lg:hidden" onError={e => { e.target.style.display = 'none'; }} />
                    </div>

                    <h1 className="text-2xl font-semibold tracking-tight mb-1 text-center">Iniciar sesión</h1>
                    <p className="text-sm text-muted-foreground mb-6 text-center">Accede con tu nickname + PIN de 4 dígitos.</p>

                    <div className="space-y-4">
                        <div>
                            <label className="text-xs text-muted-foreground font-medium">Nickname / OkUser</label>
                            <Input
                                value={nickname}
                                onChange={(e) => setNickname(e.target.value)}
                                placeholder="tu_nickname"
                                data-testid="login-nickname-input"
                                autoFocus
                            />
                            <p className="mt-1 text-[11px] text-amber-600 flex items-center gap-1">
                                <AlertCircle className="h-3 w-3 shrink-0" />
                                Tu OkUser/Nickname es sensible a las mayúsculas y minúsculas
                            </p>
                        </div>

                        <div>
                            <label className="text-xs text-muted-foreground font-medium">PIN (4 dígitos)</label>
                            <PinKeypad onChange={setPin} onComplete={(p) => submit(p)} error={err} />
                            <p className="mt-1 text-[11px] text-muted-foreground">
                                ¿Es tu primer acceso? Usa el PIN inicial <span className="font-mono font-semibold">9999</span>.
                            </p>
                        </div>

                        <Button className="w-full" size="lg" onClick={() => submit()} disabled={busy} data-testid="login-submit-button">
                            {busy ? 'Ingresando…' : 'Entrar'}
                        </Button>

                        {/* Registration CTA */}
                        <div className="rounded-xl border bg-muted/30 px-4 py-3 text-center space-y-2">
                            <p className="text-xs text-muted-foreground">
                                Si aún no tienes cuenta en okgo, regístrate, ¡es muy fácil!
                            </p>
                            <Button
                                asChild
                                size="sm"
                                className="w-full bg-red-600 hover:bg-red-700 text-white font-semibold"
                            >
                                <a
                                    href="https://app.okgo.mx/signup?q=signup&type=Cliente&step=1"
                                    target="_blank"
                                    rel="noreferrer"
                                >
                                    Regístrate
                                </a>
                            </Button>
                        </div>

                        <div className="text-center text-xs text-muted-foreground">
                            ¿Eres administrador? <Link to="/superadmin" className="text-[hsl(var(--primary))]">Ir a superadmin</Link>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
